import { SizableText, Stack, YStack, interaction } from '@mythical/ui'
import { LandingCollectibleItem } from 'app/blocks/landing/landing-collectibles'
import {
  getQuarterCode,
  QuarterCodeText,
} from 'app/components/primitive/QuarterCodeText'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { mapColor } from 'app/utils/mapColors'
import { useMemo } from 'react'
import { SolitoImage } from 'solito/image'

type BadgeType = 'current' | 'past' | 'upcoming' | null

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function toDate(value?: string | null): Date | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function toOrdinal(value: number): string {
  const mod100 = value % 100
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`

  const mod10 = value % 10
  if (mod10 === 1) return `${value}st`
  if (mod10 === 2) return `${value}nd`
  if (mod10 === 3) return `${value}rd`
  return `${value}th`
}

function formatMonthDayUTC(
  value?: string | null,
  options?: { ordinal?: boolean },
): string | null {
  const parsed = toDate(value)
  if (!parsed) return null

  const month = MONTH_NAMES[parsed.getUTCMonth()]
  const day = parsed.getUTCDate()
  return `${month} ${options?.ordinal ? toOrdinal(day) : day}`
}

function getCollectibleBadge(item: LandingCollectibleItem): BadgeType {
  const now = new Date()
  const from = toDate(item.availableFrom)
  const until = toDate(item.availableUntil)

  if (from && until) {
    if (now >= from && now <= until) return 'current'
    if (now > until) return 'past'
    if (now < from) return 'upcoming'
  } else if (from && !until) {
    if (now >= from) return 'current'
    return 'upcoming'
  }

  return null
}

function getCollectibleSubtitle(item: LandingCollectibleItem): string {
  const badge = getCollectibleBadge(item)

  if (badge === 'current' && item.availableUntil) {
    const endDate = formatMonthDayUTC(item.availableUntil)
    if (!endDate) return ''
    return `Join All Access by ${endDate}`
  }

  if (badge === 'past' && item.availableUntil) {
    const endDate = formatMonthDayUTC(item.availableUntil, { ordinal: true })
    if (!endDate) return ''
    return `Join All Access by ${endDate}`
  }

  if (badge === 'upcoming' && item.availableFrom) {
    const startDate = formatMonthDayUTC(item.availableFrom)
    if (!startDate) return ''
    return `Available on ${startDate}`
  }

  return ''
}

export type CollectThemAllCardProps = {
  item: LandingCollectibleItem
}

export const CollectThemAllCard = ({ item }: CollectThemAllCardProps) => {
  const backgroundColour = mapColor(item.backgroundColour)
  const imageUrl =
    item.thumbnail?.sizes?.square?.url || item.thumbnail?.url || ''
  const href = item.slug ? `/collectible/${item.slug}` : ''

  const subtitle = useMemo(() => getCollectibleSubtitle(item), [item])
  const quarterCode = getQuarterCode(item.quarter)

  return (
    <SmartLink
      Component={YStack}
      flex={1}
      animation="quick"
      bg="$bgTertiary"
      overflow="hidden"
      br="$5"
      p="$3"
      gap="$3"
      $gtSm={{ gap: '$4' }}
      {...(href && item.hasContent && { href, ...interaction.opacity })}
    >
      <YStack bg={backgroundColour} br="$5" p="$4" overflow="hidden">
        <YStack pos="relative" w="100%" aspectRatio={1}>
          <SolitoImage
            src={imageUrl}
            fill
            contentFit="cover"
            sizes="(min-width: 768px) 450px, 277px"
            alt="Thumbnail"
          />
        </YStack>
        <Tag
          position="absolute"
          top="$2.5"
          left="$2.5"
          variant="white"
          displaySize="md"
          pointerEvents="none"
        >
          FREE WITH ALL ACCESS
        </Tag>

        {quarterCode && (
          <Stack
            pos="absolute"
            bottom="$4"
            right="$4"
            ai="flex-end"
            pointerEvents="none"
          >
            <QuarterCodeText
              quarter={quarterCode}
              textColor="$fgBrand"
              outlineColor="$fgPrimary"
              textProps={{
                $gtSm: {
                  size: '$10.5',
                  lineHeight: '$10.5',
                },
              }}
            />
          </Stack>
        )}
      </YStack>
      <YStack gap="$1.5">
        <SizableText ff="$title" size="$m" $gtSm={{ size: '$xxl' }}>
          {item.title}
        </SizableText>

        {subtitle && (
          <SizableText size="$s" color="$fgSecondary" $gtSm={{ size: '$m' }}>
            {subtitle}
          </SizableText>
        )}
      </YStack>
    </SmartLink>
  )
}
